import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Timestamp } from "@bufbuild/protobuf";
import { AlertTriangle, Copy, Key, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Role } from "../../gen/rbt/cloud/v1alpha1/organization/organization_pb";
import { useOrganization } from "../../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";
import { APIKeyView } from "../../gen/rbt/cloud/v1alpha1/user/user_pb";
import { useUser } from "../../gen/rbt/cloud/v1alpha1/user/user_rbt_react";

interface APIKeysSectionProps {
  organizationId: string;
  currentUserId: string;
}

function formatDate(timestamp: Timestamp | undefined): string {
  if (!timestamp) {
    return "Unknown";
  }
  const date = timestamp.toDate();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function APIKeysSection({
  organizationId,
  currentUserId,
}: APIKeysSectionProps) {
  // All Reboot hooks must come before React hooks. When using suspense,
  // Reboot hooks may throw promises that cause React to re-render. If React
  // hooks are interleaved, the hook order can change between renders,
  // causing "change in the order of Hooks" errors.
  // ISSUE: https://github.com/reboot-dev/mono/issues/5197
  const { useGetMembers } = useOrganization({ id: organizationId });
  const { response: membersResponse, aborted: membersAborted } = useGetMembers(
    {},
    { suspense: true }
  );

  const { useGetAPIKeysForOrganization, createAPIKey, deleteAPIKey } = useUser({
    id: currentUserId,
  });
  const { response: apiKeysResponse, aborted: apiKeysAborted } =
    useGetAPIKeysForOrganization({ organizationId }, { suspense: true });

  // React hooks come after all Reboot hooks.
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState("");
  const [createError, setCreateError] = useState("");

  // Show error toasts in an effect to avoid updating state during
  // render (which causes "Cannot update a component while rendering
  // a different component" warnings).
  useEffect(() => {
    if (membersAborted) {
      toast({
        title: "Error",
        description:
          membersAborted.message || "Failed to load organization members.",
        variant: "destructive",
      });
    }
  }, [membersAborted, toast]);

  useEffect(() => {
    if (apiKeysAborted) {
      toast({
        title: "Error",
        description: apiKeysAborted.message || "Failed to load API keys.",
        variant: "destructive",
      });
    }
  }, [apiKeysAborted, toast]);

  if (membersAborted || apiKeysAborted) {
    return null;
  }

  const members = membersResponse.members;
  const currentUserRole = members[currentUserId].role;
  const apiKeys: APIKeyView[] = apiKeysResponse.apiKeys ?? [];

  const handleDeleteClick = (keyId: string) => {
    setKeyToDelete(keyId);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!keyToDelete) return;

    setIsDeleting(true);
    const { aborted } = await deleteAPIKey({ apiKeyId: keyToDelete });
    setIsDeleting(false);

    setShowDeleteDialog(false);
    setKeyToDelete(null);

    if (aborted) {
      toast({
        title: "Error",
        description: aborted.message || "Failed to delete API key.",
        variant: "destructive",
      });
    }
  };

  const handleCreateKey = async () => {
    setCreateError("");

    const trimmedName = newKeyName.trim();
    const existingNames = apiKeys.map((key) => key.name);
    if (existingNames.includes(trimmedName)) {
      setCreateError(`An API key named "${trimmedName}" already exists.`);
      return;
    }

    setIsCreating(true);
    const { response, aborted } = await createAPIKey({
      name: trimmedName,
      organizationId,
    });
    setIsCreating(false);

    if (aborted) {
      console.error("Create API key aborted:", aborted.error, aborted.message);
      setCreateError(aborted.message || "Failed to create API key.");
      return;
    }
    if (response) {
      setGeneratedKey(response.bearerToken);
    }
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
    setNewKeyName("");
    setGeneratedKey("");
    setCreateError("");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Keys</CardTitle>
        <CardDescription>
          Manage your API keys for accessing the Reboot Cloud CLI.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {currentUserRole === Role.VIEWER && (
          <Alert className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              As a 'Viewer' of this organization, you cannot use your API keys
              to bring <code className="font-mono text-primary">up</code>{" "}
              applications. Ask your admin to promote your account to 'Editor'
              or 'Owner' to be granted this ability.
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-4">
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create New API Key
          </Button>
        </div>

        {apiKeys.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No API keys yet. Create one to get started.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <TableRow key={apiKey.apiKeyId}>
                  <TableCell>
                    <div className="font-medium text-sm flex items-center">
                      <Key className="h-4 w-4 mr-2 text-muted-foreground" />
                      {apiKey.name || "Unnamed Key"}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(apiKey.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(apiKey.apiKeyId)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete API Key
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this API key? This action cannot
              be undone. Any applications or scripts using this key will no
              longer be able to authenticate.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseCreateDialog();
          }
        }}
      >
        <DialogContent>
          {generatedKey ? (
            <>
              <div className="space-y-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Make sure to copy your API key now. You won't be able to see
                    it again!
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>Your API Key</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={generatedKey}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedKey);
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCloseCreateDialog}>Done</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Create New API Key</DialogTitle>
                <DialogDescription>
                  Enter a name for your new API key. This helps you identify the
                  key later.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="keyName">Key Name</Label>
                  <Input
                    id="keyName"
                    placeholder="e.g. Development, CI/CD, Production"
                    value={newKeyName}
                    onChange={(e) => {
                      setNewKeyName(e.target.value);
                      setCreateError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newKeyName.trim()) {
                        handleCreateKey();
                      }
                    }}
                    disabled={isCreating}
                  />
                  {createError && (
                    <p className="text-sm text-destructive">{createError}</p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleCloseCreateDialog}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateKey}
                  disabled={isCreating || !newKeyName.trim()}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Key"
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
